import {
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  Timestamp,
  type DocumentSnapshot,
  type FieldValue,
  type Unsubscribe,
} from 'firebase/firestore';
import { auth, firestore } from '../firebase';

export const PUSH_PREFERENCE_CATEGORIES = [
  'repairWorkflow',
  'chat',
  'reviewInvitations',
  'community',
  'suggestions',
  'specializations',
  'productFeedback',
  'serviceAndAdminOperations',
] as const;

export type PushPreferenceCategory = (typeof PUSH_PREFERENCE_CATEGORIES)[number];

export type PushPreferenceValues = Record<PushPreferenceCategory, boolean>;

export interface PushPreferencesV1 {
  schemaVersion: 1;
  push: PushPreferenceValues;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export type PushPreferencesReadResult =
  | { kind: 'missing'; push: PushPreferenceValues }
  | { kind: 'present'; data: PushPreferencesV1; push: PushPreferenceValues }
  | { kind: 'unavailable'; error: unknown };

export class PushPreferencesUnavailableError extends Error {
  code = 'push-preferences-unavailable';

  constructor(message = 'Push preferences are unavailable.', cause?: unknown) {
    super(message);
    this.name = 'PushPreferencesUnavailableError';
    if (cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = cause;
    }
  }
}

const ALL_ENABLED_PUSH_VALUES: PushPreferenceValues = {
  repairWorkflow: true,
  chat: true,
  reviewInvitations: true,
  community: true,
  suggestions: true,
  specializations: true,
  productFeedback: true,
  serviceAndAdminOperations: true,
};

export const createDefaultPushPreferenceValues = (): PushPreferenceValues => ({
  ...ALL_ENABLED_PUSH_VALUES,
});

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const hasExactKeys = (value: Record<string, unknown>, keys: readonly string[]): boolean => {
  const actualKeys = Object.keys(value);
  return (
    actualKeys.length === keys.length &&
    keys.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  );
};

export const isValidPushPreferencesV1 = (value: unknown): value is PushPreferencesV1 => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['schemaVersion', 'push', 'createdAt', 'updatedAt'])
  ) {
    return false;
  }
  if (
    value.schemaVersion !== 1 ||
    !(value.createdAt instanceof Timestamp) ||
    !(value.updatedAt instanceof Timestamp)
  ) {
    return false;
  }
  if (!isRecord(value.push) || !hasExactKeys(value.push, PUSH_PREFERENCE_CATEGORIES)) {
    return false;
  }
  const push = value.push;
  return PUSH_PREFERENCE_CATEGORIES.every((category) => typeof push[category] === 'boolean');
};

const clonePushValues = (value: PushPreferenceValues): PushPreferenceValues => ({ ...value });

export const getPushPreferencesDocumentRef = (uid = auth.currentUser?.uid || '') => {
  const normalizedUid = String(uid || '').trim();
  if (!normalizedUid || auth.currentUser?.uid !== normalizedUid) {
    throw new PushPreferencesUnavailableError('Brak aktywnego użytkownika dla preferencji push.');
  }
  return doc(firestore, 'users', normalizedUid, 'notificationPreferences', 'current');
};

const readSnapshot = (snapshot: DocumentSnapshot): PushPreferencesReadResult => {
  if (!snapshot.exists()) {
    return { kind: 'missing', push: createDefaultPushPreferenceValues() };
  }
  const value = snapshot.data();
  if (!isValidPushPreferencesV1(value)) {
    return {
      kind: 'unavailable',
      error: new PushPreferencesUnavailableError(
        'Zapis preferencji push ma nieobsługiwany format.'
      ),
    };
  }
  return { kind: 'present', data: value, push: clonePushValues(value.push) };
};

export const readPushPreferencesSnapshot = (
  snapshot: DocumentSnapshot
): PushPreferencesReadResult => {
  return readSnapshot(snapshot);
};

export const subscribeToPushPreferences = (
  uid: string,
  onUpdate: (result: PushPreferencesReadResult) => void,
  onError?: (error: unknown) => void
): Unsubscribe => {
  let unsubscribe: Unsubscribe = () => undefined;
  try {
    const preferenceRef = getPushPreferencesDocumentRef(uid);
    unsubscribe = onSnapshot(
      preferenceRef,
      (snapshot) => {
        if (snapshot.metadata?.hasPendingWrites) {
          return;
        }
        try {
          onUpdate(readSnapshot(snapshot));
        } catch (error) {
          onUpdate({ kind: 'unavailable', error });
        }
      },
      (error) => {
        onUpdate({ kind: 'unavailable', error });
        onError?.(error);
      }
    );
  } catch (error) {
    onUpdate({ kind: 'unavailable', error });
    onError?.(error);
  }
  return unsubscribe;
};

type PushPreferencesWriteData = {
  schemaVersion: 1;
  push: PushPreferenceValues;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
};

export const updatePushPreferenceCategory = async (
  category: PushPreferenceCategory,
  enabled: boolean
): Promise<PushPreferencesV1> => {
  const uid = String(auth.currentUser?.uid || '').trim();
  if (!uid) {
    throw new PushPreferencesUnavailableError('Zaloguj się, aby zmienić preferencje push.');
  }
  if (!PUSH_PREFERENCE_CATEGORIES.includes(category)) {
    throw new PushPreferencesUnavailableError('Nieznana kategoria preferencji push.');
  }

  const preferenceRef = getPushPreferencesDocumentRef(uid);
  await runTransaction(firestore, async (transaction) => {
    const snapshot = await transaction.get(preferenceRef);
    const current = readSnapshot(snapshot);
    let nextData: PushPreferencesWriteData;

    if (current.kind === 'unavailable') {
      throw current.error;
    }

    if (current.kind === 'missing') {
      const push = createDefaultPushPreferenceValues();
      push[category] = enabled;
      nextData = {
        schemaVersion: 1,
        push,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
    } else {
      const push = clonePushValues(current.data.push);
      push[category] = enabled;
      nextData = {
        schemaVersion: 1,
        push,
        createdAt: current.data.createdAt,
        updatedAt: serverTimestamp(),
      };
    }

    transaction.set(preferenceRef, nextData);
  });

  // A transaction resolving is not itself a UI readback. Read the canonical
  // document once more so callers never settle on a locally fabricated value.
  const readback = readSnapshot(await getDoc(preferenceRef));
  if (readback.kind !== 'present') {
    throw readback.kind === 'unavailable'
      ? readback.error
      : new PushPreferencesUnavailableError('Nie udało się potwierdzić zapisu preferencji push.');
  }
  return readback.data;
};

export const __pushPreferencesInternals = {
  allEnabled: createDefaultPushPreferenceValues,
  clonePushValues,
  readSnapshot,
};
